import Sidebar from "../components/Sidebar";
import React, {useState} from "react";
import {Link, useParams} from "react-router-dom";
import {DataGrid} from "@mui/x-data-grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {useQuery} from "react-query";
import {makeQueryString} from "../APIs/request";
import {EmployeeAction} from "../components/actions/EmployeeAction";
import {UploadExcel} from "../components/UploadExcel";
import {usePaginationParams} from "../hooks/usePaginationParams";
import {useMe} from "../hooks/useMe";
import "../styles/list.scss";
import API from "../APIs/API";
import moment from "moment";
import {ADMIN_LOCATION_LEVEL_USER_OPTIONS} from "../constants";
import { toast } from "react-toastify";
import { generateDetailsReport, generateAttachedReport, generateUnattachedReport } from "../utils/printReport";

const employeeColumns = [
  {
    field: "employee_no",
    headerName: "Employee No",
    width: 150,
  },
  {
    field: "name_report",
    headerName: "Name Report",
    width: 200,
  },
  {
    field: "occup_pos_title",
    headerName: "Occup Pos Title",
    width: 200,
  },
  {
    field: "position_status",
    headerName: "Position Status",
    width: 160,
    sortable: false,
    renderCell: ({ row }) => (
      <span style={{ fontWeight: 600, color: row.position_attached !== false ? "#1a7f3c" : "#b44a00" }}>
        {row.position_attached !== false ? "Attached" : "Unattached"}
      </span>
    ),
  },
  {
    field: "gender",
    headerName: "Gender",
    width: 100,
  },
  {
    field: "date_of_birth",
    headerName: "Date of Birth",
    width: 130,
    renderCell: ({row}) => {
      return (
        <>
          <p>{row.date_of_birth ? moment(row.date_of_birth).format("YYYY-MM-DD") : ""}</p>
        </>
      );
    },
  },
  {
    field: "age",
    headerName: "Age",
    width: 80,
  },
  {
    field: "email",
    headerName: "Employee Email ID",
    width: 220,
  },
  {
    field: "phone_number",
    headerName: "Phone Number",
    width: 160,
  },
  {
    field: "mobile_number",
    headerName: "Mobile Number",
    width: 160,
  },
  {
    field: "status",
    headerName: "Employee Portal Login Status",
    width: 220,
    renderCell: ({row}) => {
      return (
        <>
          <p>{row.status === true ? "Active" : "Disable"}</p>
        </>
      );
    },
  },
];

const Employees = ({type}) => {
  const {admin_location_id} = useParams();
  const {gridParams, queryParams} = usePaginationParams();
  const [adminNoDesc, setAdminNoDesc] = useState("All");
  const [paypointDesc, setPaypointDesc] = useState("All");
  const {permissions, user: loggedUser} = useMe();
  const [printing, setPrinting] = useState(null); // 'details' | 'attached' | 'unattached'

  const fetchAllForPDF = async () => {
    const search = {};
    if (adminNoDesc !== 'All') search['account_no_desc'] = adminNoDesc;
    if (paypointDesc !== 'All') search['paypoint_desc'] = paypointDesc;

    const PAGE = 100;
    let allRows = [];
    let offset = 0;

    while (true) {
      // offset=0 is filtered by makeQueryString (!0 === true), which is fine — backend defaults to 0
      const qs = makeQueryString({ limit: PAGE, offset }, search, { admin_location_id });
      const result = await new API().getAllEmployees(qs);
      const page = result?.rows ?? [];
      allRows = allRows.concat(page);
      if (page.length < PAGE || allRows.length >= (result?.count ?? 0)) break;
      offset += PAGE;
    }

    return allRows;
  };

  const handlePrint = async (type) => {
    setPrinting(type);
    try {
      const rows = await fetchAllForPDF();
      const desc = adminLocation?.admin_desc ?? '';

      // Build context label for the footer based on active filters
      let filterLabel = desc;
      if (adminNoDesc !== 'All') {
        filterLabel = `${desc} - ${adminNoDesc}`;
        if (paypointDesc !== 'All') {
          filterLabel = `${desc} - ${adminNoDesc} - ${paypointDesc}`;
        }
      }

      if (type === 'details')     generateDetailsReport(rows, desc, filterLabel);
      if (type === 'attached')    generateAttachedReport(rows, desc, filterLabel);
      if (type === 'unattached')  generateUnattachedReport(rows, desc, filterLabel);
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setPrinting(null);
    }
  };

  const pageUserType = type === "Non-Teacher" ? "non-teachers" : "teachers";
  const {data: adminLocation} = useQuery(
    ["admin_location", admin_location_id],
    {
      queryFn: async () => {
        const result = await new API().getAdminLocationById(admin_location_id);
        if (result) {
          return result["data"];
        }
        return null;
      }
    }
  );
  const {data: accountNoDescList = []} = useQuery(["account_no_desc_list"], {
    queryFn: async () => {
      const result = await new API().getAccountNoDescList(admin_location_id);
      if (result) {
        if (loggedUser?.admin_location_level_user === ADMIN_LOCATION_LEVEL_USER_OPTIONS.ACCOUNT_NO_DESC_USER) {
          setAdminNoDesc(loggedUser.account_no_desc);
        }
        return ["All", ...result["data"]];
      }
      return ["All"];
    }
  });
  const {data: employees = [], isFetching} = useQuery(
    [
      "employees",
      adminNoDesc,
      paypointDesc,
      makeQueryString(queryParams, {
        admin_location_id,
      }),
    ], {
      queryFn: async () => {
        let search = {};
        if (adminNoDesc !== 'All') {
          search["account_no_desc"] = adminNoDesc;
        }
        if (paypointDesc !== 'All') {
          search["paypoint_desc"] = paypointDesc;
        }
        const queryString = makeQueryString(queryParams, search, {
          admin_location_id,
        });
        const result = await new API().getAllEmployees(queryString);
        if (result != null) {
          return result;
        }
        return [];
      }
    },
    {keepPreviousData: true}
  );
  const {data: paypointDescList = []} = useQuery(["paypoint_desc_list"],
    {
      queryFn: async () => {
        const result = await new API().getPaypointDescList(admin_location_id, adminNoDesc);
        if (result) {
          if (loggedUser.admin_location_level_user === ADMIN_LOCATION_LEVEL_USER_OPTIONS.PAYPOINT_DESC_USER) {
            setAdminNoDesc(loggedUser.paypoint_desc);
          }
          return ["All", ...result["data"]];
        }
        return ["All"];
      },
      enabled: adminNoDesc !== "All",
    }
  );

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      width: 120,
      sortable: false,
      renderCell: ({row}) => (
        <EmployeeAction row={row}/>
      ),
    },
  ];

  const transformedRows = employees?.rows?.map((row) => ({...row, ...(!row?.occup_pos_title && {occup_pos_title: 'Unattached'} )}))

  return (
    <div className="wrapper">
      <div className="sidebar">
        <Sidebar/>
      </div>
      <div className="content">
        <div className="list">
          <div className="listContainer">
            <div className="datatable">
              <Link to={`/${pageUserType}`} className="backButtonSection">
                <KeyboardBackspaceIcon className="backButton"/>
              </Link>
              <br/>
              <br/>

              <div className="datatableTitle">
                {`Details for ${adminLocation?.admin_desc ?? ''} Employees`}
                <div>
                  {permissions.super && (
                    <>
                      <Link
                        to={`/${pageUserType}/${admin_location_id}/add`}
                        className="link"
                      >
                        <button className="addBtn">Add New Employee</button>
                      </Link>
                      <UploadExcel adminLocationId={admin_location_id}/>
                    </>
                  )}
                </div>
              </div>

              <div className="searchSection">
                <div className="row">
                  <div className="col col-sm-4">
                    <TextField
                      id="outlined-disabled"
                      label="Admin desc"
                      fullWidth
                      InputProps={{
                        readOnly: true,
                      }}
                      value={adminLocation?.admin_desc ?? ""}
                    />
                  </div>
                  <div className="col col-sm-4">
                    <Autocomplete
                      disablePortal
                      id="combo-box"
                      options={accountNoDescList}
                      fullWidth
                      disabled={(loggedUser?.admin_location_level_user === ADMIN_LOCATION_LEVEL_USER_OPTIONS.ACCOUNT_NO_DESC_USER || loggedUser?.admin_location_level_user === ADMIN_LOCATION_LEVEL_USER_OPTIONS.PAYPOINT_DESC_USER)}
                      renderInput={(params) => (
                        <TextField {...params} label="Account no desc"/>
                      )}
                      onChange={(e, value) =>
                        setAdminNoDesc(value)
                      }
                      disableClearable
                      value={adminNoDesc}
                    />
                  </div>
                  <div className="col col-sm-4">
                    <Autocomplete
                      hidden={adminNoDesc === "All"}
                      disablePortal
                      id="combo-box"
                      options={paypointDescList}
                      fullWidth
                      disabled={(loggedUser?.admin_location_level_user === ADMIN_LOCATION_LEVEL_USER_OPTIONS.PAYPOINT_DESC_USER)}
                      renderInput={(params) => (
                        <TextField {...params} label="Paypoint desc"/>
                      )}
                      onChange={(e, value) =>
                        setPaypointDesc(value)
                      }
                      disableClearable
                      value={paypointDesc}
                    />
                  </div>
                </div>
              </div>
              {/* ── PDF Report Buttons ── */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <br />
                <button
                  style={{
                    backgroundImage: "linear-gradient(135deg, rgb(54, 36, 121) 10%, rgb(128, 31, 141))",
                    border: "0px",
                    borderRadius: "10px",
                    color: "#ffffff",
                    display: "inline-block",
                    fontSize: "14px",
                    margin: "10px 0px",
                    outline: "0px",
                    padding: "8px 20px",
                    cursor: "pointer",
                  }}
                  disabled={!!printing}
                  onClick={() => handlePrint('details')}
                >
                  {printing === 'details' ? 'Generating…' : 'Print Details Report'}
                </button>
                <br />
                <button
                  style={{
                    backgroundImage: "linear-gradient(135deg, rgb(54, 36, 121) 10%, rgb(128, 31, 141))",
                    border: "0px",
                    borderRadius: "10px",
                    color: "#ffffff",
                    display: "inline-block",
                    fontSize: "14px",
                    margin: "10px 0px",
                    outline: "0px",
                    padding: "8px 20px",
                    cursor: "pointer",
                  }}
                  disabled={!!printing}
                  onClick={() => handlePrint('attached')}
                >
                  {printing === 'attached' ? 'Generating…' : 'Print Attached Report'}
                </button>
                <br />
                <button
                  style={{
                    backgroundImage: "linear-gradient(135deg, rgb(54, 36, 121) 10%, rgb(128, 31, 141))",
                    border: "0px",
                    borderRadius: "10px",
                    color: "#ffffff",
                    display: "inline-block",
                    fontSize: "14px",
                    margin: "10px 0px",
                    outline: "0px",
                    padding: "8px 20px",
                    cursor: "pointer",
                  }}
                  disabled={!!printing}
                  onClick={() => handlePrint('unattached')}
                >
                  {printing === 'unattached' ? 'Generating…' : 'Print Unattached Report'}
                </button>
              </div>

              <DataGrid
                className="datagrid"
                rows={transformedRows || []}
                columns={actionColumn.concat(employeeColumns)}
                rowCount={employees?.count ?? 0}
                loading={isFetching}
                {...gridParams}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Employees;
